<template>
  <AdminLayout>
    <div class="max-w-6xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-emerald-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">업데이트 노트 관리</h1>
            <p class="text-sm text-slate-500">파트너스 대시보드에 표시되는 업데이트 노트를 관리합니다</p>
          </div>
        </div>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          업데이트 등록
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">로딩 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="updates.length === 0" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-600 mb-2">등록된 업데이트가 없습니다</h3>
        <p class="text-slate-400 mb-4">새로운 기능이나 변경사항을 등록해보세요.</p>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          업데이트 등록
        </button>
      </div>

      <!-- 업데이트 목록 -->
      <div v-else class="space-y-4">
        <div
          v-for="update in updates"
          :key="update.id"
          class="bg-white rounded-2xl p-5 shadow-sm"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-2">
                <span :class="[
                  'px-2 py-0.5 text-xs font-semibold rounded-full',
                  getTypeBadgeClass(update.type)
                ]">
                  {{ getTypeLabel(update.type) }}
                </span>
                <span class="text-xs text-slate-400">{{ update.version }}</span>
                <span :class="[
                  'px-2 py-0.5 text-xs font-semibold rounded-full',
                  update.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                ]">
                  {{ update.isActive ? '노출 중' : '비활성' }}
                </span>
              </div>
              <h3 class="text-lg font-semibold text-slate-800 mb-1">{{ update.title }}</h3>
              <p class="text-sm text-slate-500 mb-2">{{ update.description }}</p>
              <p class="text-xs text-slate-400">{{ formatDate(update.createdAt) }}</p>
            </div>
            <div class="flex items-center gap-2 ml-4">
              <button
                @click="toggleActive(update)"
                :disabled="processing === update.id"
                :class="[
                  'px-3 py-1.5 text-sm rounded-lg transition',
                  update.isActive
                    ? 'text-amber-600 hover:bg-amber-50'
                    : 'text-green-600 hover:bg-green-50'
                ]"
              >
                {{ update.isActive ? '비활성화' : '활성화' }}
              </button>
              <button
                @click="openEditModal(update)"
                class="px-3 py-1.5 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
              >
                수정
              </button>
              <button
                @click="deleteUpdate(update)"
                :disabled="processing === update.id"
                class="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 업데이트 등록/수정 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div class="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" @click.stop>
              <h3 class="text-lg font-semibold text-slate-800 mb-4">
                {{ editingUpdate ? '업데이트 수정' : '업데이트 등록' }}
              </h3>

              <form @submit.prevent="saveUpdate" class="space-y-4">
                <!-- 유형 선택 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">유형 *</label>
                  <div class="grid grid-cols-4 gap-2">
                    <button
                      v-for="type in updateTypes"
                      :key="type.value"
                      type="button"
                      @click="form.type = type.value"
                      :class="[
                        'px-3 py-2 rounded-lg text-sm font-medium transition',
                        form.type === type.value
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      ]"
                    >
                      {{ type.label }}
                    </button>
                  </div>
                </div>

                <!-- 버전 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">버전</label>
                  <input
                    v-model="form.version"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="v1.2.0"
                  />
                </div>

                <!-- 제목 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">제목 *</label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="업데이트 제목"
                    required
                  />
                </div>

                <!-- 설명 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">설명</label>
                  <textarea
                    v-model="form.description"
                    rows="3"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    placeholder="업데이트 상세 설명"
                  ></textarea>
                </div>

                <!-- 활성화 여부 -->
                <div class="flex items-center gap-3">
                  <input
                    v-model="form.isActive"
                    type="checkbox"
                    id="isActive"
                    class="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <label for="isActive" class="text-sm text-slate-700">업데이트 활성화 (대시보드에 노출)</label>
                </div>

                <!-- 버튼 -->
                <div class="flex gap-2 pt-4">
                  <button
                    type="button"
                    @click="closeModal"
                    class="flex-1 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    :disabled="!canSubmit || submitting"
                    class="flex-1 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition disabled:opacity-50"
                  >
                    {{ submitting ? '저장 중...' : (editingUpdate ? '수정하기' : '등록하기') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'PartnerUpdateManageView',
  components: {
    AdminLayout
  },
  setup() {
    const loading = ref(true)
    const updates = ref([])
    const processing = ref(null)
    const showModal = ref(false)
    const editingUpdate = ref(null)
    const submitting = ref(false)

    const updateTypes = [
      { value: 'feature', label: '신기능' },
      { value: 'improvement', label: '개선' },
      { value: 'fix', label: '버그수정' },
      { value: 'notice', label: '안내' }
    ]

    const form = ref({
      type: 'feature',
      version: '',
      title: '',
      description: '',
      isActive: true
    })

    const canSubmit = computed(() => {
      return form.value.title.trim()
    })

    const loadUpdates = async () => {
      try {
        loading.value = true
        const q = query(
          collection(db, 'partnerUpdates'),
          orderBy('createdAt', 'desc')
        )
        const snapshot = await getDocs(q)
        updates.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('업데이트 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    const getTypeLabel = (type) => {
      const found = updateTypes.find(t => t.value === type)
      return found ? found.label : type
    }

    const getTypeBadgeClass = (type) => {
      const classes = {
        feature: 'bg-blue-100 text-blue-700',
        improvement: 'bg-emerald-100 text-emerald-700',
        fix: 'bg-amber-100 text-amber-700',
        notice: 'bg-violet-100 text-violet-700'
      }
      return classes[type] || 'bg-slate-100 text-slate-600'
    }

    const openCreateModal = () => {
      editingUpdate.value = null
      form.value = {
        type: 'feature',
        version: '',
        title: '',
        description: '',
        isActive: true
      }
      showModal.value = true
    }

    const openEditModal = (update) => {
      editingUpdate.value = update
      form.value = {
        type: update.type || 'feature',
        version: update.version || '',
        title: update.title || '',
        description: update.description || '',
        isActive: update.isActive !== false
      }
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      editingUpdate.value = null
      form.value = {
        type: 'feature',
        version: '',
        title: '',
        description: '',
        isActive: true
      }
    }

    const saveUpdate = async () => {
      if (!canSubmit.value) return

      submitting.value = true
      try {
        const updateData = {
          type: form.value.type,
          version: form.value.version.trim(),
          title: form.value.title.trim(),
          description: form.value.description.trim(),
          isActive: form.value.isActive,
          updatedAt: new Date().toISOString()
        }

        if (editingUpdate.value) {
          await updateDoc(doc(db, 'partnerUpdates', editingUpdate.value.id), updateData)
          alert('업데이트가 수정되었습니다.')
        } else {
          updateData.createdAt = new Date().toISOString()
          await addDoc(collection(db, 'partnerUpdates'), updateData)
          alert('업데이트가 등록되었습니다.')
        }

        closeModal()
        await loadUpdates()
      } catch (error) {
        console.error('업데이트 저장 실패:', error)
        alert('저장에 실패했습니다: ' + error.message)
      } finally {
        submitting.value = false
      }
    }

    const toggleActive = async (update) => {
      processing.value = update.id
      try {
        await updateDoc(doc(db, 'partnerUpdates', update.id), {
          isActive: !update.isActive,
          updatedAt: new Date().toISOString()
        })
        await loadUpdates()
      } catch (error) {
        console.error('상태 변경 실패:', error)
        alert('상태 변경에 실패했습니다.')
      } finally {
        processing.value = null
      }
    }

    const deleteUpdate = async (update) => {
      if (!confirm('이 업데이트를 삭제하시겠습니까?')) return

      processing.value = update.id
      try {
        await deleteDoc(doc(db, 'partnerUpdates', update.id))
        await loadUpdates()
        alert('업데이트가 삭제되었습니다.')
      } catch (error) {
        console.error('삭제 실패:', error)
        alert('삭제에 실패했습니다.')
      } finally {
        processing.value = null
      }
    }

    onMounted(loadUpdates)

    return {
      loading,
      updates,
      processing,
      showModal,
      editingUpdate,
      submitting,
      form,
      updateTypes,
      canSubmit,
      formatDate,
      getTypeLabel,
      getTypeBadgeClass,
      openCreateModal,
      openEditModal,
      closeModal,
      saveUpdate,
      toggleActive,
      deleteUpdate
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
